import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type { ClinicResponse, InvoiceResponse, IssueInvoiceRequest } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useLocalisedName } from '../../i18n/localisedName';
import { money } from '../catalog/money';

/**
 * Manual subscription invoicing (FR-1102, DEC-047).
 *
 * The default view is what is owed rather than everything ever issued, because the question this
 * screen exists to answer every month is "who has not paid". Selecting a clinic switches to its
 * full history.
 *
 * The branch count and the amount are not typed in: they come from the branch-activation trail and
 * the clinic's agreed price (RULE-016). An operator can still override both for a correction, and
 * whatever is used is copied onto the invoice.
 */
export function PlatformInvoicesPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [clinicId, setClinicId] = useState('');

  const clinics = useApiQuery<ClinicResponse[]>(queryKeys.platformClinics(), '/platform/clinics');

  const invoices = useApiQuery<InvoiceResponse[]>(
    clinicId ? queryKeys.platformClinicInvoices(clinicId) : queryKeys.platformInvoices(),
    clinicId ? `/platform/clinics/${clinicId}/invoices` : '/platform/invoices',
  );

  const issue = useApiMutation<InvoiceResponse, { readonly clinicId: string } & IssueInvoiceRequest>(
    ({ clinicId: target, ...body }) => ({
      path: `/platform/clinics/${target}/invoices`,
      options: { method: 'POST', body },
    }),
    { invalidates: [queryKeys.platformInvoices(), ['platform', 'clinics']] },
  );

  const markPaid = useApiMutation<InvoiceResponse, string>(
    (invoiceId) => ({ path: `/platform/invoices/${invoiceId}/payment`, options: { method: 'POST' } }),
    { invalidates: [queryKeys.platformInvoices(), ['platform', 'clinics']] },
  );

  return (
    <>
      <PageHeader title={t('nav.items.invoices')} description={t('platform.invoicesDescription')} />

      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('platform.clinic')}</span>
          <select
            className="field__input"
            value={clinicId}
            onChange={(event) => setClinicId(event.target.value)}
          >
            <option value="">{t('platform.allOpenInvoices')}</option>
            {(clinics.data ?? []).map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {localised.displayName(clinic, clinic.legalName)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {clinicId ? (
        <form
          className="panel"
          style={{ marginBlockEnd: 'var(--space-5)' }}
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            issue.mutate({
              clinicId,
              periodStart: String(form.get('periodStart') ?? ''),
              periodEnd: String(form.get('periodEnd') ?? ''),
              dueOn: String(form.get('dueOn') ?? ''),
            });
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('platform.periodStart')}</span>
              <input type="date" name="periodStart" className="field__input numeric" required />
            </label>
            <label className="field">
              <span className="field__label">{t('platform.periodEnd')}</span>
              <input type="date" name="periodEnd" className="field__input numeric" required />
            </label>
            <label className="field">
              <span className="field__label">{t('platform.dueOn')}</span>
              <input type="date" name="dueOn" className="field__input numeric" required />
            </label>
          </div>

          {issue.error ? <ErrorState error={issue.error} /> : null}

          <div style={{ marginBlockStart: 'var(--space-4)' }}>
            <button type="submit" className="button button--primary" disabled={issue.isPending}>
              {issue.isPending ? t('branches.saving') : t('platform.issueInvoice')}
            </button>
            <p className="field__hint">{t('platform.issueInvoiceHint')}</p>
          </div>
        </form>
      ) : null}

      <AsyncSection
        query={invoices}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('platform.noInvoicesTitle')}
        emptyDescription={t('platform.noInvoicesDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platform.invoiceNumber')}</th>
                  <th scope="col">{t('platform.period')}</th>
                  <th scope="col">{t('platform.branches')}</th>
                  <th scope="col">{t('platform.total')}</th>
                  <th scope="col">{t('platform.dueOn')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((invoice) => (
                  <tr key={invoice.id}>
                    <th scope="row" className="ltr">
                      {invoice.number}
                    </th>
                    <td className="numeric">
                      {invoice.periodStart} → {invoice.periodEnd}
                    </td>
                    <td className="numeric">{invoice.billableBranches}</td>
                    <td className="numeric">
                      {money(invoice.total)}
                    </td>
                    <td className="numeric">{invoice.dueOn}</td>
                    <td>
                      {invoice.overdue ? (
                        <span className="badge badge--danger">{t('platform.invoiceStatus.OVERDUE')}</span>
                      ) : (
                        <span className={invoice.status === 'PAID' ? 'badge' : 'badge badge--muted'}>
                          {t(`platform.invoiceStatus.${invoice.status ?? 'ISSUED'}`)}
                        </span>
                      )}
                    </td>
                    <td>
                      {invoice.status === 'ISSUED' ? (
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={markPaid.isPending}
                          onClick={() => markPaid.mutate(invoice.id ?? '')}
                        >
                          {t('platform.markPaid')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {markPaid.error ? <ErrorState error={markPaid.error} /> : null}

      <p className="field__hint" style={{ marginBlockStart: 'var(--space-4)' }}>
        {t('platform.markPaidHint')}
      </p>
    </>
  );
}
